/* Feb-2026 · Reusable "Publish Everywhere" bolt-on button.
 *
 * Renders a compact icon-button that opens the ZernioPublishDialog.
 * Auto-hides when Zernio is not configured or the admin flag is off.
 * Drop into any surface where an author has a finished asset to share:
 *   • IsbnManagerDialog (cover reveal)
 *   • EditorPanel (chapter drop / launch teaser)
 *   • MyRoyaltiesDialog (milestone celebration)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import ZernioSmartComposer from '@/components/dialogs/ZernioSmartComposer';
import { API_BASE } from '@/lib/api';

export default function PublishEverywhereButton({
  size          = 'sm',
  variant       = 'outline',
  label         = null,
  initialCaption = '',
  initialMedia  = null,
  bookId        = null,
  title         = '',
  synopsis      = '',
  destUrl       = null,
  testid        = 'publish-everywhere-btn',
}) {
  const { t } = useTranslation();
  const [open, setOpen]     = useState(false);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/zernio/config`, { credentials: 'include' })
      .then((r) => r.ok ? r.json() : null)
      .then(setConfig)
      .catch(() => setConfig(null));
  }, []);

  const visible = useMemo(
    () => !!(config && config.configured && config.flag_on),
    [config],
  );
  if (!visible) return null;

  return (
    <>
      <Button
        size={size}
        variant={variant}
        onClick={() => setOpen(true)}
        data-testid={testid}
        className="gap-1.5"
      >
        <Share2 className="h-3.5 w-3.5" /> {label || t('publish.publishEverywhere')}
      </Button>
      <ZernioSmartComposer
        open={open}
        onOpenChange={setOpen}
        title={title || initialCaption}
        synopsis={synopsis}
        initialMedia={initialMedia}
        bookId={bookId}
        destUrl={destUrl}
      />
    </>
  );
}
